import React from 'react';
import PropTypes from 'prop-types';
import {
  withStyles,
  Icon,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { withRouter } from 'react-router-dom';

import styles, { inlineStyles } from './styles';
import propTypes from '../../../../../utils/propTypes';
import { formatDateString } from '../../../../../utils/dateHelper';
import StatusBadgeList from '../../../../widgets/StatusBadgeList';
import TagList from '../../../../widgets/TagList';
import ArchiveDeleteEventTemplateModal from '../../../../modals/ArchiveDeleteEventTemplateModal';
import RestoreEventTemplateModal from '../../../../modals/RestoreEventTemplateModal';

const getDataGridSortModel = ({ sort: paginationSort }) => paginationSort.map((sortModel) => {
  const [field, sort] = sortModel.split(',');
  return {
    field,
    sort,
  };
});

const getPagableSortModel = (sortModel) => sortModel.map((model) => (`${model.field},${model.sort}`));

export class EventTemplateListComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      actionAnchorElement: null,
      actionId: null,
      openArchiveDeleteModal: false,
      openRestoreModal: false,
      selectedEventTemplateId: null,
    };
  }

  handlePageChange = (pageable) => {
    const {
      pagination,
      loading,
    } = this.props;
    if (!loading) {
      this.props.onPaginationUpdate(this.props.includeArchived, {
        ...pagination,
        page: pageable.page,
      });
    }
  }

  handleSortChange = ({ sortModel } = {}) => {
    const {
      pagination,
      loading,
    } = this.props;
    const sort = getPagableSortModel(sortModel);
    if (
      !loading
        && JSON.stringify(sort) !== JSON.stringify(pagination.sort)
    ) {
      this.props.onPaginationUpdate(this.props.includeArchived, {
        ...pagination,
        sort,
      });
    }
  }

  handleActionClick = ({
    actionAnchorElement,
    actionId,
  }) => {
    this.setState({
      actionAnchorElement,
      actionId,
    });
  }

  handleActionClose = () => {
    this.setState({
      actionAnchorElement: null,
      actionId: null,
    });
  }

  handleWorkflowSelection = ({ rows: selectedWorkflows }) => {
    this.props.onSelectedWorkflowsChange(selectedWorkflows);
  }

  handleCloseArchiveDeleteModal = () => {
    this.setState({
      selectedEventTemplateId: null,
      openArchiveDeleteModal: false,
    });
  }

  handleCloseRestoreModal = () => {
    this.setState({
      selectedEventTemplateId: null,
      openRestoreModal: false,
    });
  }

  handleOpenArchiveDeleteModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openArchiveDeleteModal: true,
    });
  }

  handleOpenRestoreModal = (eventTemplateId) => {
    this.setState({
      selectedEventTemplateId: eventTemplateId,
      openRestoreModal: true,
    });
  }

  render() {
    const {
      classes,
      eventTemplates,
      loading,
      pagination,
      statuses,
      history,
      readOnly,
      showIdColumn,
    } = this.props;

    const {
      actionAnchorElement,
      actionId,
      openArchiveDeleteModal,
      openRestoreModal,
      selectedEventTemplateId,
    } = this.state;

    const readOnlyColumns
      = readOnly
        ? []
        : [{
          field: 'id',
          headerName: ' ',
          width: 44,
          renderCell: (params) => (
            <div>
              <IconButton
                aria-label="actions"
                style={inlineStyles.actionButton}
                onClick={(event) => {
                  event.preventDefault();
                  this.handleActionClick({
                    actionAnchorElement: event.currentTarget,
                    actionId: params.row.id,
                  });
                }}
              >
                <Icon fontSize="small">more_vert</Icon>
              </IconButton>
              <Menu
                id="simple-menu"
                anchorEl={actionAnchorElement}
                keepMounted
                open={actionId === params.row.id}
                onClose={this.handleActionClose}
              >
                <MenuItem
                  onClick={() => {
                    this.handleActionClose();
                    history.push({ pathname: `/settings/workflows/${params.row.id}` });
                  }}
                >
                  <ListItemIcon>
                    <Icon>edit</Icon>
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    this.handleActionClose();
                    history.push({ pathname: `/settings/workflows/new?copy=${params.row.id}` });
                  }}
                >
                  <ListItemIcon>
                    <Icon>file_copy</Icon>
                  </ListItemIcon>
                  <ListItemText>Copy</ListItemText>
                </MenuItem>
                {
                  !params.row.archived
                    ? (
                      <MenuItem
                        onClick={() => {
                          this.handleActionClose();
                          this.handleOpenArchiveDeleteModal(params.row.id);
                        }}
                      >
                        <ListItemIcon>
                          <Icon>delete</Icon>
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                      </MenuItem>
                    )
                    : (
                      <MenuItem
                        onClick={() => {
                          this.handleActionClose();
                          this.handleOpenRestoreModal(params.row.id);
                        }}
                      >
                        <ListItemIcon>
                          <Icon>restore_from_trash</Icon>
                        </ListItemIcon>
                        <ListItemText>Restore</ListItemText>
                      </MenuItem>
                    )
                }
              </Menu>
            </div>
          ),
        }];

    const idColumns
      = showIdColumn
        ? [{
          field: 'id',
          headerName: 'ID',
          width: 80,
        }]
        : [];

    const columns = [
      {
        field: 'name',
        headerName: 'Name',
        description: 'The workflow name used when creating new events',
        width: 280,
        sortComparator: (a, b) => a.replaceAll(/[^a-zA-Z0-9]/g, '') - b.replaceAll(/[^a-zA-Z0-9]/g, ''),
        renderCell: (params) => [
          <Tooltip title={params.row.description} key={`event-${params.row.id}-description`}>
            <div style={inlineStyles.nameContainer}>
              <div style={inlineStyles.name}>
                {
                  params.row.archived
                    && <Icon style={inlineStyles.archivedIcon}>delete</Icon>
                }
                {params.row.name}
              </div>
              <Typography variant="caption" style={inlineStyles.caption}>{params.row.description}</Typography>
            </div>
          </Tooltip>,
        ],
      },
      {
        field: 'workflowTags',
        headerName: 'Tags',
        description: 'The tags used in the workflow',
        width: 350,
        renderCell: (params) => (
          <TagList tags={params.row.tags} size="small" />
        ),
      },
      {
        field: 'workflowStatuses',
        headerName: 'Statuses',
        description: 'The statuses used in this workflow',
        width: 280,
        renderCell: (params) => (
          <StatusBadgeList
            statuses={statuses}
            displayedStatuses={params.row.workflowStatuses}
            statusIdField="statusId"
            mini
            key={`event-${params.row.id}-status-badge-list`}
          />
        ),
      },
      {
        field: 'updatedDate',
        headerName: 'Updated',
        description: 'The date a workflow was last updated',
        width: 180,
        renderCell: (params) => (
          params.row.updatedDate
            ? formatDateString(params.row.updatedDate)
            : formatDateString(params.row.createdDate)
        ),
      },
      {
        field: 'updatedByEmail',
        headerName: 'Updated By',
        description: 'The user who last update a workflow',
        width: 180,
        renderCell: (params) => (
          params.row.updatedByEmail
            ? params.row.updatedByEmail
            : params.row.createdByEmail
        ),
      },
    ];

    return [
      <DataGrid
        rows={eventTemplates.content}
        columns={[
          ...readOnlyColumns,
          ...idColumns,
          ...columns,
        ]}
        rowCount={eventTemplates.totalElements}
        page={pagination.page}
        pageSize={pagination.size}
        pagination
        checkboxSelection={!readOnly}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        onPageChange={this.handlePageChange}
        onSortModelChange={this.handleSortChange}
        onSelectionChange={this.handleWorkflowSelection}
        sortingOrder={['asc', 'desc']}
        sortModel={getDataGridSortModel(pagination)}
        autoHeight
        disableSelectionOnClick
        className={classes.container}
        key="event-templaet-datagrid"
      />,
      <ArchiveDeleteEventTemplateModal
        eventTemplateIds={[selectedEventTemplateId]}
        handleCloseModal={this.handleCloseArchiveDeleteModal}
        isOpen={openArchiveDeleteModal}
        key="event-template-archive-modal"
      />,
      <RestoreEventTemplateModal
        eventTemplateIds={[selectedEventTemplateId]}
        handleCloseModal={this.handleCloseRestoreModal}
        isOpen={openRestoreModal}
        key="event-template-restore-modal"
      />,
    ];
  }
}

EventTemplateListComponent.propTypes = {
  includeArchived: PropTypes.bool,
  eventTemplates: PropTypes.object,
  pagination: PropTypes.shape(propTypes.pagination),
  loading: PropTypes.bool,
  classes: PropTypes.object,
  onPaginationUpdate: PropTypes.func,
  onSelectedWorkflowsChange: PropTypes.func,
  statuses: PropTypes.array,
  history: PropTypes.object,
  readOnly: PropTypes.bool,
  showIdColumn: PropTypes.bool,
};

EventTemplateListComponent.defaultProps = {
  includeArchived: false,
  eventTemplates: { content: [] },
  loading: false,
  classes: {},
  onPaginationUpdate: () => {},
  onSelectedWorkflowsChange: () => {},
  pagination: {
    page: 0,
    size: 10,
    search: '',
    sort: ['name,asc'],
  },
  statuses: [],
  history: { push: () => {} },
  readOnly: false,
  showIdColumn: false,
};

export default withRouter(withStyles(styles)(EventTemplateListComponent));
